/* global window */

import React, {Fragment, Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Ratings from 'react-ratings-declarative';
import numeral from 'numeral';

import Button from '../Button';
import Quantity from '../Quantity';

import styles from './styles.module.css';

class ProductRow extends Component {
    static propTypes = {
        asin: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        price: PropTypes.string.isRequired,
        rating: PropTypes.number.isRequired,
        image: PropTypes.string,
        quantity: PropTypes.number.isRequired,
        compatible: PropTypes.bool,
        recommended: PropTypes.bool,
        onQuantityChange: PropTypes.func.isRequired,
    }

    static defaultProps = {
        image: 'http://via.placeholder.com/200x200/?text=No Photo',
        recommended: false,
        compatible: false,
    }

    state = {
        expanded: 0,
        origin: 'center left',
        translate: null,
        overlayPos: {
            top: 0,
            left: 0,
        },
    }

    componentDidMount() {
        window.addEventListener('resize', this.repositionImage);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.repositionImage);
    }

    expand = () => {
        const {
            left, top, width, height,
        } = this.image.getBoundingClientRect();
        const viewportXCenter = window.innerWidth / 2;
        const viewportYCenter = window.innerHeight / 2;

        const origin = [];
        let translate = null;

        if(left < viewportXCenter - width)
            origin.push('left');
        else if(left >= viewportXCenter - width && left <= viewportXCenter + width)
            origin.push('center');
        else if(left > viewportXCenter + width)
            origin.push('right');

        if(origin[0] !== 'center')
            translate = 'x';

        if(top < viewportYCenter - height)
            origin.push('10%');
        else if(top >= viewportYCenter - height && top <= viewportYCenter + height)
            origin.push('center');
        else if(top > viewportYCenter + height)
            origin.push('90%');

        if(origin[1] !== 'center')
            translate += 'y';

        this.setState({
            expanded: 1,
            origin: origin.join(' '),
            translate,
            overlayPos: {
                top: this.overlay.offsetTop,
                left: this.overlay.offsetLeft,
            },
        }, () => {
            setTimeout(() => {
                this.setState({
                    expanded: 2,
                });
            }, 50);
        });
    }

    close = (evt) => {
        if(evt.target !== this.background)
            return;

        this.setState({
            expanded: false,
        });
    }

    changeQuantity = (newQuantity) => {
        const {
            asin, onQuantityChange,
        } = this.props;

        onQuantityChange(asin, newQuantity);
    }

    repositionImage = () => {
        const {expanded} = this.state;

        if(expanded === 0)
            return;

        this.setState({
            overlayPos: {
                top: this.overlay.offsetTop,
                left: this.overlay.offsetLeft,
            },
        });
    }

    renderTags() {
        const {compatible, recommended} = this.props;

        return (
            <>
                {(compatible && !recommended) && (
                    <div className={classnames(styles.tag, styles.compatible)}>
                        Compatible
                    </div>
                )}
                {recommended && (
                    <div className={classnames(styles.tag, styles.recommended)}>
                        Recommended
                    </div>
                )}
            </>
        );
    }

    renderOverlay() {
        const {
            asin, title, description, price, rating, image, quantity,
        } = this.props;
        const {
            expanded, overlayPos, origin, translate,
        } = this.state;

        return (
            <div
                className={classnames(styles.background, {
                    [styles.show]: expanded === 2,
                })}
                onClick={this.close}
                ref={(el) => { this.background = el; }}
                tabIndex="-1"
                role="button"
                onKeyDown={(evt) => (evt.keyCode === 13 || evt.keyCode === 32) && this.close()}
            >
                <div
                    className={classnames(styles.overlay, {
                        [styles.translateX]: translate === 'x',
                        [styles.translateY]: translate === 'y',
                        [styles.translateXY]: translate === 'xy',
                    })}
                    style={{
                        transformOrigin: origin,
                    }}
                    ref={(el) => { this.overlay = el; }}
                >
                    <div className={styles.details}>
                        {this.renderTags()}
                        <div className={styles.title}>{title}</div>
                        <div className={styles.price}>{numeral(price).format('$0,0.00')}</div>
                        <div className={styles.rating}>
                            <Ratings
                                rating={rating}
                                widgetRatedColors="#03c9ff"
                                widgetDimensions="13px"
                                widgetSpacings="1.5px"
                                className={styles.stars}
                            >
                                <Ratings.Widget />
                                <Ratings.Widget />
                                <Ratings.Widget />
                                <Ratings.Widget />
                                <Ratings.Widget />
                            </Ratings>
                        </div>
                    </div>
                    <Quantity
                        className={styles.quantity}
                        onChange={this.changeQuantity}
                        amount={quantity}
                    />
                    <div className={styles.description}>
                        {description && (<p>{description}</p>)}
                        <Button
                            href={`https://amzn.com/${asin}`}
                            className={styles.link}
                            target="_blank"
                        >
                            View on Amazon
                        </Button>
                    </div>
                </div>
                <div
                    className={classnames(styles.imageClone, {
                        [styles.translateX]: translate === 'x',
                        [styles.translateY]: translate === 'y',
                        [styles.translateXY]: translate === 'xy',
                    })}
                    style={{
                        ...overlayPos,
                        transformOrigin: origin,
                        backgroundImage: `url(${image})`,
                    }}
                />
            </div>
        );
    }

    render() {
        const {
            title, price, rating, image, quantity,
        } = this.props;
        const {
            expanded, origin, translate,
        } = this.state;

        return (
            <Fragment>
                {this.renderOverlay()}
                <div
                    className={classnames(styles.container, {
                        [styles.showClone]: expanded > 0,
                        [styles.expanded]: expanded === 2,
                    })}
                >
                    <div
                        className={classnames(styles.image, {
                            [styles.translateX]: translate === 'x',
                            [styles.translateY]: translate === 'y',
                            [styles.translateXY]: translate === 'xy',
                        })}
                        style={{
                            transformOrigin: origin,
                            backgroundImage: `url(${image})`,
                        }}
                        ref={(el) => { this.image = el; }}
                        onClick={this.expand}
                        tabIndex="0"
                        role="button"
                        onKeyDown={
                            (evt) => (
                                evt.keyCode === 13 || evt.keyCode === 32
                            ) && this.expand()
                        }
                    />
                    <div className={styles.details}>
                        {this.renderTags()}
                        <div
                            className={styles.title}
                            onClick={this.expand}
                            tabIndex="0"
                            role="button"
                            onKeyDown={
                                (evt) => (
                                    evt.keyCode === 13 || evt.keyCode === 32
                                ) && this.expand()
                            }
                        >
                            {title}
                        </div>
                        <div className={styles.price}>
                            {numeral(price).format('$0,0.00')}
                        </div>
                        <div className={styles.rating}>
                            <Ratings
                                rating={rating}
                                widgetRatedColors="#03c9ff"
                                widgetDimensions="13px"
                                widgetSpacings="1.5px"
                                className={styles.stars}
                            >
                                <Ratings.Widget />
                                <Ratings.Widget />
                                <Ratings.Widget />
                                <Ratings.Widget />
                                <Ratings.Widget />
                            </Ratings>
                        </div>
                    </div>
                    <Quantity
                        className={styles.quantity}
                        onChange={this.changeQuantity}
                        amount={quantity}
                    />
                </div>
            </Fragment>
        );
    }
}

export default ProductRow;
